import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import api from "api";
import { useNavigate, useLocation } from "react-router-dom";
import "./Projects.css";
import PageHeader from "components/layout/page-header/PageHeader";
import { IconButton, Tooltip } from "@material-ui/core";
import { ArrowBack as ArrowBackIcon } from "@material-ui/icons";
import AppModal from "components/ui/AppModal/AppModal";

const CACHE_DURATION = 30 * 60 * 1000;

const PRODUCT_LABELS = {
  fotos:        "📸 Fotos",
  filmagens:    "🎬 Filmagens",
  gemeo_digital:"🏗️ Gêmeo Digital",
  tour_360:     "🔄 Tour 360°",
  geoview:       "🗺️ GeoView",
  ortomosaico:  "🗺️ GeoView", // legado
  geotiff:      "🗺️ GeoView", // legado
  bim:          "📐 BIM",
  relatorios:   "📋 Relatórios"
};

const PRODUCT_COLORS = {
  fotos:        { bg: "#dbeafe", border: "#bfdbfe", text: "#1e40af" },
  filmagens:    { bg: "#ffedd5", border: "#fed7aa", text: "#9a3412" },
  gemeo_digital:{ bg: "#dcfce7", border: "#bbf7d0", text: "#166534" },
  tour_360:     { bg: "#f3e8ff", border: "#e9d5ff", text: "#6b21a8" },
  geoview:       { bg: "#ccfbf1", border: "#99f6e4", text: "#115e59" },
  ortomosaico:  { bg: "#ccfbf1", border: "#99f6e4", text: "#115e59" }, // legado
  geotiff:      { bg: "#ccfbf1", border: "#99f6e4", text: "#115e59" }, // legado
  bim:          { bg: "#e0f2fe", border: "#bae6fd", text: "#075985" },
  relatorios:   { bg: "#ffe4e6", border: "#fecdd3", text: "#9f1239" },
};

const DEFAULT_COLOR = { bg: "#f1f5f9", border: "#e2e8f0", text: "#334155" };

const Projects = ({ email, token }) => {
  const [projects, setProjects]             = useState([]);
  const [planData, setPlanData]             = useState(null);
  const [azureUsageData, setAzureUsageData] = useState(null);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);
  const [view, setView]                     = useState("list");
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchText, setSearchText]         = useState("");
  const [dateRange, setDateRange]           = useState("all");
  const [progressData, setProgressData]     = useState({});
  const [loadingShares, setLoadingShares]       = useState({});
  const [loadingDownloads, setLoadingDownloads] = useState({});
  const [loadingTransfers, setLoadingTransfers] = useState({});
  const [shareModal, setShareModal] = useState({ open: false, url: "", copied: false });
  const [modal, setModal] = useState({ open: false, variant: "info", title: "", message: "", confirmText: "OK", cancelText: null, onConfirm: null, onCancel: null });
  const [transferModal, setTransferModal] = useState({ open: false, projectName: "", date: "", product: "", email: "" });
  const [selectionMode, setSelectionMode]         = useState(false);
  const [selectedNames, setSelectedNames]         = useState(new Set());
  const [dateSelectionMode, setDateSelectionMode] = useState(false);
  const [selectedDateKeys, setSelectedDateKeys]   = useState(new Set());

  const [bulkTransferModal, setBulkTransferModal] = useState({ open: false, email: "", mode: "projects" });
  const modalResolveRef = useRef(null);
  const navigate  = useNavigate();
  const location  = useLocation();

  // Evita cache vazio reaproveitado entre contas no mesmo navegador.
  const cacheSuffix = (email || "").trim().toLowerCase() || "anon";
  const keyCachedProjects = `cachedProjects_${cacheSuffix}`;
  const keyCachedPlanData = `cachedPlanData_${cacheSuffix}`;
  const keyCachedAzureUsage = `cachedAzureUsageData_${cacheSuffix}`;
  const keyProjectsCacheTimestamp = `projectsCacheTimestamp_${cacheSuffix}`;

  /* ── Modal helpers ────────────────────────────────────────────────────── */
  const openModal = (config) =>
    new Promise(resolve => {
      modalResolveRef.current = resolve;
      setModal({ confirmText: "OK", cancelText: null, onConfirm: null, onCancel: null, ...config, open: true });
    });

  const confirmModal = () => {
    setModal(m => ({ ...m, open: false }));
    modalResolveRef.current?.(true);
    modalResolveRef.current = null;
  };

  const closeModal = () => {
    setModal(m => ({ ...m, open: false }));
    modalResolveRef.current?.(false);
    modalResolveRef.current = null;
  };

  /* ── Polling: gemeo_digital progress ─────────────────────────────────── */
  useEffect(() => {
    const pollProgress = async () => {
      const itemsToPoll = [];
      projects.forEach(p => {
        p.dates?.forEach(d => {
          if (d.products?.includes("gemeo_digital") || d.products?.includes("bim")) {
            const key = `${p.name}-${d.date}`;
            const cur = progressData[key];
            if (cur === undefined || (cur >= 0 && cur < 100))
              itemsToPoll.push({ name: p.name, date: d.date, key });
          }
        });
      });
      if (!itemsToPoll.length) return;

      const newProgress = { ...progressData };
      let hasUpdates = false;
      for (const item of itemsToPoll) {
        try {
          const { data } = await api.get(`/projects/status/${item.name}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const { status, progress } = data;
          const newVal =
            ["Processando","Na fila","Salvando","Baixando","Download Concluído"].includes(status)
              ? (progress || 0)
              : status === "Concluído" ? 100 : -1;
          if (newProgress[item.key] !== newVal) {
            newProgress[item.key] = newVal;
            hasUpdates = true;
          }
          await new Promise(r => setTimeout(r, 500));
        } catch (err) {
          console.error(`Erro ao verificar status de ${item.name}:`, err);
        }
      }
      if (hasUpdates) setProgressData(newProgress);
    };

    const hasActive = Object.values(progressData).some(v => v >= 0 && v < 100);
    let intervalId;
    if (hasActive) { pollProgress(); intervalId = setInterval(pollProgress, 10000); }
    return () => clearInterval(intervalId);
  }, [projects, token, progressData]);

  /* ── Fetch data ───────────────────────────────────────────────────────── */
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const cachedProjects   = localStorage.getItem(keyCachedProjects);
        const cachedPlanData   = localStorage.getItem(keyCachedPlanData);
        const cachedAzureUsage = localStorage.getItem(keyCachedAzureUsage);
        const cachedTimestamp  = localStorage.getItem(keyProjectsCacheTimestamp);
        const now             = new Date().getTime();
        const cacheAge        = cachedTimestamp ? now - cachedTimestamp : Infinity;
        const shouldRefresh   = location.state?.refresh;
        const openProjectName = location.state?.openProject;

        const parsedCache = cachedProjects ? JSON.parse(cachedProjects) : null;
        const isCacheValid= Array.isArray(parsedCache);
        // Lista vazia em cache costuma ser resposta antiga/errada (ex.: placeholders Azure incompatíveis). Refetch.
        const cacheTemProjetos = isCacheValid && parsedCache.length > 0;

        if (!shouldRefresh && cacheTemProjetos && cachedPlanData && cachedAzureUsage && cacheAge < CACHE_DURATION) {
          const loaded = parsedCache;
          console.log("[v4d:Projects] cache local (sem refetch API)", {
            email,
            cacheSuffix,
            keyCachedProjects,
            projetosNoCache: loaded?.length ?? 0,
            cacheAgeMs: cacheAge,
            amostra: Array.isArray(loaded) ? loaded.slice(0, 2) : loaded,
          });
          setProjects(loaded);
          setPlanData(JSON.parse(cachedPlanData));
          setAzureUsageData(JSON.parse(cachedAzureUsage));
          const initialProgress = {};
          loaded.forEach(p => p.dates?.forEach(d => {
            if (d.products?.includes("gemeo_digital")) {
              initialProgress[`${p.name}-${d.date}`] = d.gemeo_progress ?? 100;
            }
          }));
          setProgressData(initialProgress);
          if (openProjectName) {
            const found = loaded.find(p => p.name === openProjectName);
            if (found) { setSelectedProject(found); setView("project"); }
            navigate(location.pathname, { state: undefined, replace: true });
          }
          setLoading(false);
          return;
        }

        if (shouldRefresh) navigate(location.pathname, { state: undefined, replace: true });

        const [planRes, projRes, usageRes] = await Promise.all([
          api.get("/plans/user",   { headers: { Authorization: `Bearer ${token}` } }),
          api.get("/projects/user",{ headers: { Authorization: `Bearer ${token}` } }),
          api.get("/plans/usage",  { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        const rawProjects = projRes.data;
        console.log("[v4d:Projects] resposta HTTP /projects/user", {
          status: projRes.status,
          email,
          tipo: rawProjects === null ? "null" : Array.isArray(rawProjects) ? "array" : typeof rawProjects,
          tamanhoOuKeys: Array.isArray(rawProjects)
            ? { length: rawProjects.length }
            : rawProjects && typeof rawProjects === "object"
              ? { keys: Object.keys(rawProjects) }
              : rawProjects,
          payloadBruto: rawProjects,
        });

        const userPlanData = planRes.data[0];
        setPlanData(userPlanData);
        setAzureUsageData(usageRes.data);

        let loaded;
        if (Array.isArray(projRes.data)) {
          loaded = projRes.data;
        } else if (projRes.data && typeof projRes.data === "object") {
          loaded = Object.entries(projRes.data).map(([projName, datesObj]) => {
            const dates = Object.entries(datesObj || {})
              .map(([date, products]) => ({
                date,
                products: Array.isArray(products) ? products.filter(p => typeof p === "string") : []
              }))
              .sort((a, b) => b.date.localeCompare(a.date));
            return { name: projName, thumbnail: null, last_date: dates[0]?.date || null, date_count: dates.length, dates };
          });
        } else {
          loaded = [];
        }

        console.log("[v4d:Projects] lista normalizada para o estado", {
          count: loaded.length,
          nomes: loaded.map(p => p?.name).filter(Boolean),
          loaded,
        });

        setProjects(loaded);

        const initialProgress = {};
        loaded.forEach(p => p.dates?.forEach(d => {
          if (d.products?.includes("gemeo_digital"))
            initialProgress[`${p.name}-${d.date}`] = d.gemeo_progress ?? 100;
            if (d.products?.includes("bim"))
              initialProgress[`${p.name}-${d.date}`] = 0;
        }));
        setProgressData(initialProgress);

        if (openProjectName) {
          const found = loaded.find(p => p.name === openProjectName);
          if (found) { setSelectedProject(found); setView("project"); }
          navigate(location.pathname, { state: undefined, replace: true });
        }

        localStorage.setItem(keyCachedProjects,          JSON.stringify(loaded));
        localStorage.setItem(keyCachedPlanData,           JSON.stringify(userPlanData));
        localStorage.setItem(keyCachedAzureUsage,         JSON.stringify(usageRes.data));
        localStorage.setItem(keyProjectsCacheTimestamp,   now);
        setError(null);
      } catch (err) {
        console.error("Erro ao buscar dados:", err);
        console.log("[v4d:Projects] erro na requisição", {
          message: err?.message,
          status: err?.response?.status,
          data: err?.response?.data,
        });
        setError("Falha ao buscar dados");
      } finally {
        setLoading(false);
      }
    };
    if (email && token) fetchData();
  }, [email, token]);

  // Garante que modo de seleção por data não fique "preso" ao mudar de tela.
  useEffect(() => {
    if (view !== "project") {
      setDateSelectionMode(false);
      setSelectedDateKeys(new Set());
    } else {
      // Ao trocar o projeto selecionado, limpa a seleção anterior.
      setDateSelectionMode(false);
      setSelectedDateKeys(new Set());
    }
  }, [view, selectedProject?.name]);

  /* ── Expiry helpers ───────────────────────────────────────────────────── */
  const getProjectExpiryInfo = (project) => {
    if (!project.is_free_plan || !project.dates) return { allExpired: false, anySoonExpiring: false, minDaysRemaining: null };
    const remainings = project.dates
      .map(d => d.days_remaining)
      .filter(d => d !== undefined && d !== null);
    if (remainings.length === 0) return { allExpired: false, anySoonExpiring: false, minDaysRemaining: null };
    const allExpired = remainings.every(r => r <= 0);
    const anySoonExpiring = remainings.some(r => r > 0 && r <= 5);
    const minDaysRemaining = Math.min(...remainings);
    return { allExpired, anySoonExpiring, minDaysRemaining };
  };

  const handleExpiredClick = async () => {
    const confirmed = await openModal({
      variant: "warning",
      title: "Projeto expirado",
      message: "Este projeto expirou após 30 dias no plano gratuito. Faça upgrade para acessar novamente.",
      confirmText: "Ver Planos",
      cancelText: "Ok",
    });
    if (confirmed) navigate("/plans");
  };

  /* ── Helpers ──────────────────────────────────────────────────────────── */
  const isDateInRange = dateStr => {
    if (dateRange === "all") return true;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return true;
    const diffDays = (Date.now() - d) / (1000 * 60 * 60 * 24);
    if (dateRange === "7d")  return diffDays <= 7;
    if (dateRange === "30d") return diffDays <= 30;
    if (dateRange === "90d") return diffDays <= 90;
    return true;
  };

  const getAllProductsForProject = project => {
    const products = new Set();
    project.dates?.forEach(d => {
      if (Array.isArray(d.products)) d.products.forEach(p => { if (typeof p === "string") products.add(p); });
    });
    return Array.from(products);
  };

  const filteredProjects = (Array.isArray(projects) ? projects : []).filter(p => {
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    if (p.name.toLowerCase().includes(q)) return true;
    if (p.dates?.some(d => d.date?.toLowerCase().includes(q))) return true;
    const allProds = getAllProductsForProject(p);
    if (allProds.some(prod => (PRODUCT_LABELS[prod] || prod).toLowerCase().includes(q))) return true;
    return false;
  });

  /* ── Bulk helpers ─────────────────────────────────────────────────────── */
  const expandProjectToItems = (project) =>
    project.dates.flatMap(d =>
      (d.products || []).map(prod => ({
        project_name: project.name,
        project_type: prod,
        date_str: d.date,
      }))
    );

  const handleToggleSelectionMode = () => {
    if (selectionMode) { setSelectionMode(false); setSelectedNames(new Set()); }
    else setSelectionMode(true);
  };

  const handleToggleSelectCard = (projectName) => {
    setSelectedNames(prev => {
      const next = new Set(prev);
      if (next.has(projectName)) next.delete(projectName);
      else next.add(projectName);
      return next;
    });
  };

  const _applyBulkDeleteResult = (deletedItems) => {
    const selectedNameToRefresh = selectedProject?.name;
    const removed = {};
    deletedItems.forEach(({ project_name, project_type, date_str }) => {
      if (!removed[project_name]) removed[project_name] = {};
      if (!removed[project_name][date_str]) removed[project_name][date_str] = new Set();
      removed[project_name][date_str].add(project_type);
    });
    setProjects(prev => {
      const updated = prev.map(p => {
        if (!removed[p.name]) return p;
        const newDates = p.dates
          .map(d => {
            const toRemove = removed[p.name]?.[d.date];
            return toRemove
              ? { ...d, products: d.products.filter(pr => !toRemove.has(pr)) }
              : d;
          })
          .filter(d => d.products.length > 0);
        return { ...p, dates: newDates, date_count: newDates.length };
      }).filter(p => p.dates.length > 0);
      localStorage.setItem(keyCachedProjects, JSON.stringify(updated));
      localStorage.setItem(keyProjectsCacheTimestamp, String(new Date().getTime()));
      localStorage.removeItem(keyCachedAzureUsage);

      if (selectedNameToRefresh) {
        const refreshed = updated.find(p => p.name === selectedNameToRefresh);
        if (refreshed) setSelectedProject(refreshed);
        else {
          setSelectedProject(null);
          setView("list");
        }
      }
      return updated;
    });
  };

  const handleCardDelete = async (project) => {
    const items = expandProjectToItems(project);
    const confirmed = await openModal({
      variant: "warning",
      title: "Excluir projeto completo?",
      message: `Remove permanentemente "${project.name}" com ${items.length} item(ns). Irreversível.`,
      confirmText: "Sim, excluir",
      cancelText: "Cancelar",
    });
    if (!confirmed) return;
    setModal({ open: true, variant: "loading", title: "Excluindo...", message: `Removendo "${project.name}"…`, confirmText: "OK", cancelText: null, onConfirm: null, onCancel: null });
    try {
      const { data } = await api.post("/projects/delete_bulk", { items }, { headers: { Authorization: `Bearer ${token}` } });
      _applyBulkDeleteResult(data.deleted);
      if (data.failed?.length > 0)
        await openModal({ variant: "warning", title: "Excluído com avisos", message: `${data.deleted.length} removidos. ${data.failed.length} falharam.` });
      else
        await openModal({ variant: "success", title: "Projeto excluído", message: `"${project.name}" removido com sucesso.` });
    } catch (err) {
      await openModal({ variant: "error", title: "Erro", message: err.response?.data?.detail || "Falha ao excluir." });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedNames.size === 0) return;
    const selProjects = projects.filter(p => selectedNames.has(p.name));
    const allItems = selProjects.flatMap(expandProjectToItems);
    const confirmed = await openModal({
      variant: "warning",
      title: `Excluir ${selectedNames.size} projeto(s)?`,
      message: `Serão removidos ${allItems.length} item(ns). Irreversível.`,
      confirmText: "Sim, excluir tudo",
      cancelText: "Cancelar",
    });
    if (!confirmed) return;
    setModal({ open: true, variant: "loading", title: "Excluindo projetos…", message: `Processando ${allItems.length} item(ns)…`, confirmText: "OK", cancelText: null, onConfirm: null, onCancel: null });
    try {
      const { data } = await api.post("/projects/delete_bulk", { items: allItems }, { headers: { Authorization: `Bearer ${token}` } });
      _applyBulkDeleteResult(data.deleted);
      setSelectedNames(new Set()); setSelectionMode(false);
      if (data.failed?.length > 0)
        await openModal({ variant: "warning", title: "Exclusão com falhas", message: `${data.deleted.length} removidos. ${data.failed.length} falharam e foram mantidos.` });
      else
        await openModal({ variant: "success", title: "Projetos excluídos", message: `${data.deleted.length} item(ns) removidos.` });
    } catch (err) {
      await openModal({ variant: "error", title: "Erro na exclusão", message: err.response?.data?.detail || "Tente novamente." });
    }
  };

  const handleBulkTransfer = () => {
    setBulkTransferModal({ open: true, email: "", mode: "projects" });
  };

  const handleBulkTransferConfirm = async () => {
    const { email, mode } = bulkTransferModal;
    if (!email) return;
    setBulkTransferModal(m => ({ ...m, open: false }));

    let transferItems = [];
    if (mode === "dates") {
      if (!selectedProject) return;
      const selDates = Array.from(selectedDateKeys);
      transferItems = selDates.flatMap(dateStr => {
        const d = selectedProject.dates?.find(dd => dd.date === dateStr);
        const prods = (d?.products || []).filter(
          prod => prod !== "relatorios" && typeof prod === "string"
        );
        return prods.map(prod => ({
          project_name: selectedProject.name,
          product: prod,
          project_date: dateStr
        }));
      });
    } else {
      const selProjects = projects.filter(p => selectedNames.has(p.name));
      transferItems = selProjects.flatMap(p =>
        p.dates.flatMap(d =>
          (d.products || [])
            .filter(prod => prod !== "relatorios")
            .map(prod => ({ project_name: p.name, product: prod, project_date: d.date }))
        )
      );
    }

    const total = transferItems.length;
    if (total === 0) return;

    setModal({ open: true, variant: "loading", title: `Transferindo 1 / ${total}…`, message: `Enviando para ${email}…`, confirmText: "OK", cancelText: null, onConfirm: null, onCancel: null });

    let successCount = 0;
    const failedItems = [];
    for (let i = 0; i < transferItems.length; i++) {
      const item = transferItems[i];
      setModal(m => ({ ...m, title: `Transferindo ${i + 1} / ${total}…` }));
      try {
        await api.post(
          "/transfers/request",
          { project_name: item.project_name, product: item.product, project_date: item.project_date, recipient_email: email },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        successCount++;
      } catch {
        failedItems.push(item);
      }
    }

    if (mode === "dates") {
      setSelectedDateKeys(new Set());
      setDateSelectionMode(false);
    } else {
      setSelectedNames(new Set());
      setSelectionMode(false);
    }
    if (failedItems.length > 0)
      await openModal({ variant: "warning", title: "Transferência com falhas", message: `${successCount} solicitados. ${failedItems.length} falharam.` });
    else
      await openModal({ variant: "success", title: "Transferência solicitada!", message: `${successCount} item(ns) enviados para ${email}. O destinatário receberá notificação.` });
  };

  /* ── Date selection helpers (view project) ───────────────────────────── */
  const handleToggleDateSelectionMode = () => {
    if (dateSelectionMode) {
      setDateSelectionMode(false);
      setSelectedDateKeys(new Set());
    } else {
      setDateSelectionMode(true);
    }
  };

  const handleToggleDateSelected = (dateStr) => {
    setSelectedDateKeys(prev => {
      const next = new Set(prev);
      if (next.has(dateStr)) next.delete(dateStr);
      else next.add(dateStr);
      return next;
    });
  };

  const getProductsForDate = (dateEntry) =>
    (dateEntry?.products || []).filter(prod => typeof prod === "string");

  const getTransferProductsForDate = (dateEntry) =>
    getProductsForDate(dateEntry).filter(prod => prod !== "relatorios");

  const handleTransferDate = (dateEntry) => {
    if (!selectedProject) return;
    if (selectedProject.name.toLowerCase() === "demo") return;

    const products = getTransferProductsForDate(dateEntry);
    const mainProd = products[0];
    if (!mainProd) return;

    handleTransferProduct(selectedProject.name, dateEntry.date, mainProd);
  };

  const handleDeleteDate = async (dateEntry) => {
    if (!selectedProject) return;
    if (selectedProject.name.toLowerCase() === "demo") return;

    const products = getProductsForDate(dateEntry);
    if (products.length === 0) return;

    const items = products.map(prod => ({
      project_name: selectedProject.name,
      project_type: prod,
      date_str: dateEntry.date
    }));

    const confirmed = await openModal({
      variant: "warning",
      title: "Excluir data?",
      message: `Remover ${dateEntry.date} (serão removidos ${items.length} produto(s)). Irreversível.`,
      confirmText: "Sim, excluir",
      cancelText: "Cancelar"
    });
    if (!confirmed) return;

    setModal({
      open: true,
      variant: "loading",
      title: "Excluindo...",
      message: `Removendo ${dateEntry.date}…`,
      confirmText: "OK",
      cancelText: null,
      onConfirm: null,
      onCancel: null
    });

    try {
      const { data } = await api.post("/projects/delete_bulk", { items }, { headers: { Authorization: `Bearer ${token}` } });
      _applyBulkDeleteResult(data.deleted);

      if (dateSelectionMode) {
        setSelectedDateKeys(new Set());
        setDateSelectionMode(false);
      }

      if (data.failed?.length > 0)
        await openModal({ variant: "warning", title: "Excluído com avisos", message: `${data.deleted.length} removidos. ${data.failed.length} falharam.` });
      else
        await openModal({ variant: "success", title: "Data excluída", message: `${dateEntry.date} removida com sucesso.` });
    } catch (err) {
      await openModal({ variant: "error", title: "Erro", message: err.response?.data?.detail || "Falha ao excluir." });
    }
  };

  const handleDateBulkTransfer = () => {
    setBulkTransferModal({ open: true, email: "", mode: "dates" });
  };

  const handleDateBulkDelete = async () => {
    if (!selectedProject) return;
    if (selectedProject.name.toLowerCase() === "demo") return;
    if (selectedDateKeys.size === 0) return;

    const dates = Array.from(selectedDateKeys);
    const items = dates.flatMap(dateStr => {
      const d = selectedProject.dates?.find(dd => dd.date === dateStr);
      const products = (d?.products || []).filter(prod => typeof prod === "string");
      return products.map(prod => ({
        project_name: selectedProject.name,
        project_type: prod,
        date_str: dateStr
      }));
    });

    if (items.length === 0) {
      await openModal({
        variant: "warning",
        title: "Nada para excluir",
        message: "As datas selecionadas não possuem produtos deletáveis."
      });
      return;
    }

    const confirmed = await openModal({
      variant: "warning",
      title: `Excluir ${dates.length} data(s)?`,
      message: `Serão removidos ${items.length} item(ns). Irreversível.`,
      confirmText: "Sim, excluir tudo",
      cancelText: "Cancelar"
    });
    if (!confirmed) return;

    setModal({
      open: true,
      variant: "loading",
      title: "Excluindo datas…",
      message: `Processando ${items.length} item(ns)…`,
      confirmText: "OK",
      cancelText: null,
      onConfirm: null,
      onCancel: null
    });

    try {
      const { data } = await api.post("/projects/delete_bulk", { items }, { headers: { Authorization: `Bearer ${token}` } });
      _applyBulkDeleteResult(data.deleted);

      setSelectedDateKeys(new Set());
      setDateSelectionMode(false);

      if (data.failed?.length > 0)
        await openModal({ variant: "warning", title: "Exclusão com falhas", message: `${data.deleted.length} removidos. ${data.failed.length} falharam e foram mantidos.` });
      else
        await openModal({ variant: "success", title: "Datas excluídas", message: `${data.deleted.length} item(ns) removidos.` });
    } catch (err) {
      await openModal({ variant: "error", title: "Erro na exclusão", message: err.response?.data?.detail || "Tente novamente." });
    }
  };

  /* ── Handlers ─────────────────────────────────────────────────────────── */
  const handleAddProjectClick = async () => {
    const total = Object.values(azureUsageData?.project_stats || {}).reduce((a, p) => a + (p.subfolders || 0), 0);
    if (total >= (planData?.max_projects || 0)) {
      const confirmed = await openModal({
        variant: "warning",
        title: "Limite de projetos atingido",
        message: "Seu plano atual não permite adicionar mais projetos.",
        confirmText: "Ver Planos",
        cancelText: "Ok",
      });
      if (confirmed) navigate("/plans");
    } else {
      navigate("/project/registration");
    }
  };

  const handleOpenProduct = (projectName, date, product) => {
    const proj = projects.find(p => p.name === projectName);
    if (proj && !proj.has_thumbnail) {
      api.post("/projects/request_thumbnail", { project_name: projectName },
        { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }

    if (product === "gemeo_digital" || product === "bim") {
      const key = `${projectName}-${date}`;
      const progress = progressData[key];
      if (progress !== undefined && progress >= 0 && progress < 100) {
        openModal({ variant: "info", title: "Processando...", message: `O projeto ${projectName} está sendo processado (${progress}%). Aguarde.` });
        return;
      } else if (progress === -1) {
        openModal({ variant: "error", title: "Erro no Processamento", message: `Erro ao processar ${projectName}. Entre em contato com o suporte.` });
        return;
      }
    }

    const folder_path = `projetos/${projectName}/${date}/${product}/`;

    if (product === "relatorios") {
      navigate("/reports/list", { state: { folder_path, project: { name: projectName, type: product }, date } });
      return;
    }
    if (product === "gemeo_digital") {
      window.location.href = `/gemeo-digital?projectName=${encodeURIComponent(projectName)}&projectType=gemeo_digital&demo=false&date=${encodeURIComponent(date)}`;
      return;
    }

    const paths = {
      tour_360: "/tour",
      filmagens: "/videoplayer",
      geoview: "/geoview",
      ortomosaico: "/geoview", // legado
      geotiff: "/geoview", // legado
      bim: "/bim"
    };
    navigate(paths[product] || "/photogallery", {
      state: { folder_path, project: { name: projectName, type: product }, demo: "false", date }
    });
  };

  const handleShare = async (folder_path, projectKey, product, projectName) => {
    if (projectName.toLowerCase() === "demo") {
      await openModal({ variant: "warning", title: "Compartilhamento não permitido", message: "Projetos demo não podem ser compartilhados." });
      return;
    }
    setLoadingShares(prev => ({ ...prev, [projectKey]: true }));
    try {
      const { data } = await api.post("/projects/create_share_token", { folder_path }, { headers: { Authorization: `Bearer ${token}` } });
      const routes = {
        tour_360: "sharedtour",
        filmagens: "sharedvideo",
        gemeo_digital: "shared",
        geoview: "sharedgeoview",
        ortomosaico: "sharedgeoview", // legado
        geotiff: "sharedgeoview", // legado
        bim: "sharedbim"
      };
      const shareUrl = `${window.location.origin}/${routes[product] || "sharedphotos"}?token=${data.token}`;
      setShareModal({ open: true, url: shareUrl, copied: false });
    } catch {
      await openModal({ variant: "error", title: "Erro", message: "Não foi possível gerar o link de compartilhamento." });
    } finally {
      setLoadingShares(prev => ({ ...prev, [projectKey]: false }));
    }
  };

  const handleCopyShareUrl = () => {
    navigator.clipboard.writeText(shareModal.url).then(() => {
      setShareModal(prev => ({ ...prev, copied: true }));
      setTimeout(() => setShareModal(prev => ({ ...prev, copied: false })), 2200);
    });
  };

  const handleDownload = async (projectName, date, product, productKey) => {
    setLoadingDownloads(prev => ({ ...prev, [productKey]: true }));
    try {
      const response = await api.post(
        "/projects/download_product",
        { project_name: projectName, date, product },
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${projectName}_${date}_${product}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      await openModal({ variant: "error", title: "Erro", message: "Não foi possível fazer o download dos arquivos." });
    } finally {
      setLoadingDownloads(prev => ({ ...prev, [productKey]: false }));
    }
  };

  const handleDeleteProduct = async (projectName, date, product) => {
    const confirmed = await openModal({
      variant: "warning",
      title: "Tem certeza?",
      message: `Excluir permanentemente "${projectName}" — ${date} — ${PRODUCT_LABELS[product] || product}?`,
      confirmText: "Sim, excluir!",
      cancelText: "Cancelar",
    });
    if (!confirmed) return;

    setModal({ open: true, variant: "loading", title: "Excluindo...", message: "Aguarde...", confirmText: "OK", cancelText: null, onConfirm: null, onCancel: null });

    try {
      const response = await api.delete("/projects/delete", {
        headers: { Authorization: `Bearer ${token}` },
        data: { project_name: projectName, project_type: product, date_str: date, is_demo: false }
      });
      if (response.status === 200) {
        setProjects(prev => {
          const updated = prev.map(p => {
            if (p.name !== projectName) return p;
            const newDates = p.dates.map(d => {
              if (d.date !== date) return d;
              return { ...d, products: d.products.filter(pr => pr !== product) };
            }).filter(d => d.products.length > 0);
            return { ...p, dates: newDates, date_count: newDates.length };
          }).filter(p => p.dates.length > 0);
          localStorage.setItem(keyCachedProjects, JSON.stringify(updated));
          localStorage.setItem(keyProjectsCacheTimestamp, new Date().getTime());
          localStorage.removeItem(keyCachedAzureUsage);
          if (selectedProject?.name === projectName) {
            const refreshed = updated.find(p => p.name === projectName);
            setSelectedProject(refreshed || null);
            if (!refreshed) setView("list");
          }
          return updated;
        });
        await openModal({ variant: "success", title: "Excluído!", message: "Removido com sucesso." });
      } else throw new Error(response.data?.message || "Falha ao excluir");
    } catch (error) {
      await openModal({ variant: "error", title: "Erro!", message: error.response?.data?.message || error.message || "Ocorreu um erro ao excluir." });
    }
  };

  const handleTransferProduct = (projectName, date, product) => {
    setTransferModal({ open: true, projectName, date, product, email: "" });
  };

  const handleTransferConfirm = async () => {
    const { projectName, date, product, email } = transferModal;
    if (!email) return;
    setTransferModal(m => ({ ...m, open: false }));
    const transferKey = `${projectName}-${date}-${product}`;
    setLoadingTransfers(prev => ({ ...prev, [transferKey]: true }));
    try {
      await api.post(
        "/transfers/request",
        { project_name: projectName, product, project_date: date, recipient_email: email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await openModal({ variant: "success", title: "Transferência solicitada!", message: "O destinatário receberá uma notificação para aceitar ou recusar." });
    } catch (err) {
      const msg = err.response?.data?.detail || "Não foi possível solicitar a transferência.";
      await openModal({ variant: "error", title: "Erro", message: msg });
    } finally {
      setLoadingTransfers(prev => ({ ...prev, [transferKey]: false }));
    }
  };

  /* ── Render: list view ────────────────────────────────────────────────── */
  const renderListView = () => (
    <>
      {/* Toolbar */}
      <div className="projects-toolbar">
        <div className="projects-search-wrap">
          <i className="fas fa-search" />
          <input
            type="text"
            placeholder="Buscar projeto..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
        </div>

        {searchText && (
          <button className="projects-clear-btn" onClick={() => setSearchText("")}>
            Limpar
          </button>
        )}

        <span className="projects-count-badge">
          {filteredProjects.length} projeto{filteredProjects.length !== 1 ? "s" : ""}
        </span>

        <button
          type="button"
          className="projects-select-btn projects-create-btn"
          onClick={handleAddProjectClick}
        >
          <i className="fas fa-plus" />
          Criar ou adicionar produto
        </button>

        <button
          className={`projects-select-btn${selectionMode ? " active" : ""}`}
          onClick={handleToggleSelectionMode}
        >
          <i className={`fas fa-${selectionMode ? "times" : "check-square"}`} />
          {selectionMode ? "Cancelar seleção" : "Selecionar"}
        </button>
      </div>

      {/* Grid */}
      <div className="projects-grid">
        {filteredProjects.length === 0 && (
          <div className="projects-empty">
            <div className="projects-empty-icon">🗂️</div>
            <p>Nenhum projeto encontrado.</p>
          </div>
        )}

        {filteredProjects.map(project => {
          const allProducts = getAllProductsForProject(project);
          const hasReports  = allProducts.includes("relatorios");
          const mainProducts= allProducts.filter(p => p !== "relatorios");
          const firstProd   = mainProducts[0] || allProducts[0];
          const colors      = PRODUCT_COLORS[firstProd] || DEFAULT_COLOR;
          const visibleChips= allProducts.slice(0, 3);
          const extraCount  = allProducts.length - visibleChips.length;
          const { allExpired, anySoonExpiring, minDaysRemaining } = getProjectExpiryInfo(project);
          const isDemo      = project.name.toLowerCase() === "demo";
          const firstDate   = project.dates?.[0];
          const mainProd    = mainProducts[0] || allProducts[0];
          const isSelected  = selectedNames.has(project.name);

          return (
            <div
              key={project.name}
              className={`project-card-v2${allExpired ? " project-card--expired" : anySoonExpiring ? " project-card--expiring" : ""}${selectionMode ? " selectable" : ""}${isSelected ? " selected" : ""}`}
              onClick={() => {
                if (selectionMode) { handleToggleSelectCard(project.name); return; }
                if (allExpired) { handleExpiredClick(); return; }
                setSelectedProject(project); setView("project");
              }}
            >
              {/* Checkbox overlay (selection mode) */}
              {selectionMode && (
                <div
                  className="card-checkbox"
                  onClick={e => { e.stopPropagation(); handleToggleSelectCard(project.name); }}
                >
                  <i className={`fas fa-${isSelected ? "check-circle" : "circle"}`} />
                </div>
              )}

              {/* Expired overlay */}
              {allExpired && (
                <div className="project-card-expired-overlay">
                  <span className="project-card-expired-badge">
                    <i className="fas fa-lock" /> Expirado
                  </span>
                </div>
              )}

              {/* Thumbnail */}
              <div className="project-card-thumb" style={{ background: allExpired ? "#f1f5f9" : colors.bg }}>
                {project.thumbnail && !allExpired ? (
                  <img src={project.thumbnail} alt={project.name} />
                ) : (
                  <div className="project-card-thumb-icon">
                    {allExpired ? "🔒" : (PRODUCT_LABELS[firstProd]?.split(" ")[0] || "📁")}
                  </div>
                )}
              </div>

              {/* Body */}
              <div className="project-card-body">
                <h3 className="project-card-name">{project.name}</h3>

                <div className="project-card-meta">
                  <span className="project-card-date">
                    <i className="fas fa-calendar-alt" />
                    {project.last_date || "—"}
                  </span>
                  {project.date_count > 1 && (
                    <span className="project-card-sessions">
                      {project.date_count} datas
                    </span>
                  )}
                </div>

                {/* Expiry warning badge */}
                {!allExpired && anySoonExpiring && minDaysRemaining !== null && (
                  <div className="project-expiry-warning">
                    <i className="fas fa-clock" />
                    Expira em {minDaysRemaining} dia{minDaysRemaining !== 1 ? "s" : ""}
                  </div>
                )}

                <div className="project-card-chips">
                  {visibleChips.map(p => {
                    const c = PRODUCT_COLORS[p] || DEFAULT_COLOR;
                    return (
                      <span
                        key={p}
                        className="product-chip"
                        style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text }}
                      >
                        <span>{PRODUCT_LABELS[p]?.split(" ")[0]}</span>
                        <span>{PRODUCT_LABELS[p]?.split(" ").slice(1).join(" ")}</span>
                      </span>
                    );
                  })}
                  {extraCount > 0 && (
                    <span className="product-chip" style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", color: "#64748b" }}>
                      +{extraCount}
                    </span>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="project-card-footer">
                <span className="card-open-btn">
                  {allExpired
                    ? <><i className="fas fa-arrow-up" /> Fazer upgrade</>
                    : <><i className="fas fa-arrow-right" /> Abrir projeto</>
                  }
                </span>

                {/* Quick actions — visible on hover, hidden in selection mode */}
                {!selectionMode && !isDemo && !allExpired && firstDate && mainProd && (
                  <div className="card-quick-actions" onClick={e => e.stopPropagation()}>
                    <Tooltip title="Transferir" arrow placement="top">
                      <button
                        className="cqa-btn"
                        onClick={() => handleTransferProduct(project.name, firstDate.date, mainProd)}
                      >
                        <i className="fas fa-share-square" />
                      </button>
                    </Tooltip>
                    <Tooltip title="Excluir projeto" arrow placement="top">
                      <button
                        className="cqa-btn cqa-btn--danger"
                        onClick={() => handleCardDelete(project)}
                      >
                        <i className="fas fa-trash-alt" />
                      </button>
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bulk toolbar */}
      {selectionMode && selectedNames.size > 0 && (
        <div className="bulk-toolbar">
          <span className="bulk-toolbar-count">
            <i className="fas fa-check-square" /> {selectedNames.size} selecionado(s)
          </span>
          <div className="bulk-toolbar-actions">
            <button className="bulk-toolbar-btn bulk-toolbar-btn--danger" onClick={handleBulkDelete}>
              <i className="fas fa-trash-alt" /> Excluir
            </button>
            <button className="bulk-toolbar-btn bulk-toolbar-btn--transfer" onClick={handleBulkTransfer}>
              <i className="fas fa-share-square" /> Transferir
            </button>
            <button className="bulk-toolbar-btn bulk-toolbar-btn--cancel" onClick={handleToggleSelectionMode}>
              <i className="fas fa-times" /> Cancelar
            </button>
          </div>
        </div>
      )}
    </>
  );

  /* ── Render: project detail view ──────────────────────────────────────── */
  const renderProjectView = () => {
    if (!selectedProject) return null;
    const filteredDates = selectedProject.dates?.filter(d => isDateInRange(d.date)) || [];
    const isDemo = selectedProject.name.toLowerCase() === "demo";

    return (
      <div className="project-detail-wrap">
        {/* Header */}
        <div className="project-detail-header">
          <Tooltip title="Voltar para Meus Projetos">
            <div
              className="project-detail-back-btn"
              onClick={() => { setView("list"); setSelectedProject(null); }}
              role="button"
              aria-label="Voltar"
            >
              <ArrowBackIcon style={{ fontSize: "1.1rem" }} />
            </div>
          </Tooltip>
          <div className="project-detail-title-block">
            <div className="project-detail-title-text">
              <div className="project-detail-label">Projeto</div>
              <h2 className="project-detail-name">{selectedProject.name}</h2>
            </div>

            <div className="project-detail-title-actions">
              <Tooltip title="Criar ou adicionar produto" arrow placement="top">
                <button
                  type="button"
                  className="projects-select-btn project-detail-create-btn"
                  onClick={handleAddProjectClick}
                  aria-label="Criar ou adicionar produto"
                >
                  <i className="fas fa-plus" /> Criar ou adicionar produto
                </button>
              </Tooltip>

              {!isDemo && (
                <Tooltip
                  title={
                    dateSelectionMode
                      ? "Cancelar seleção"
                      : "Selecionar datas para transferir/excluir"
                  }
                  arrow placement="top"
                >
                  <button
                    type="button"
                    className={`projects-select-btn${dateSelectionMode ? " active" : ""}`}
                    onClick={handleToggleDateSelectionMode}
                  >
                    <i className={`fas fa-${dateSelectionMode ? "times" : "check-square"}`} />
                    {dateSelectionMode ? "Cancelar seleção" : "Selecionar"}
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="project-detail-filter">
          <label>Período:</label>
          <select value={dateRange} onChange={e => setDateRange(e.target.value)}>
            <option value="all">Todas as datas</option>
            <option value="7d">Últimos 7 dias</option>
            <option value="30d">Últimos 30 dias</option>
            <option value="90d">Últimos 90 dias</option>
          </select>
        </div>

        {filteredDates.length === 0 && (
          <p style={{ color: "var(--text-3)", fontSize: "0.85rem", fontWeight: 500 }}>
            Nenhuma data encontrada com os filtros atuais.
          </p>
        )}

        {/* Date entries — side-by-side grid, equal height */}
        <div className="dates-grid">
        {filteredDates.map(dateEntry => {
          const gemeoKey     = `${selectedProject.name}-${dateEntry.date}`;
          const gemeoProgress= progressData[gemeoKey];
          const bimKey        = `${selectedProject.name}-${dateEntry.date}`;
          const bimProgress  = progressData[bimKey];
          const isEntryExpired = dateEntry.expired === true;
          const entryDaysRemaining = dateEntry.days_remaining;
          const isExpiringSoon = !isEntryExpired && entryDaysRemaining !== undefined && entryDaysRemaining <= 5;
          const productsForDate = getProductsForDate(dateEntry);
          const transferProductsForDate = getTransferProductsForDate(dateEntry);
          const mainTransferProd = transferProductsForDate[0];
          const canQuickTransfer = !isDemo && !!mainTransferProd;
          const canQuickDelete = !isDemo && productsForDate.length > 0;
          const isDateSelected = selectedDateKeys.has(dateEntry.date);

          return (
            <div
              key={dateEntry.date}
              className={`date-entry-card${isEntryExpired ? " date-entry--expired" : isExpiringSoon ? " date-entry--expiring" : ""}${dateSelectionMode && !isDemo ? " selectable" : ""}${isDateSelected ? " selected" : ""}`}
            >
              {dateSelectionMode && !isDemo && (
                <div
                  className="card-checkbox"
                  role="button"
                  aria-label={`Selecionar data ${dateEntry.date}`}
                  onClick={e => {
                    e.stopPropagation();
                    handleToggleDateSelected(dateEntry.date);
                  }}
                >
                  <i className={`fas fa-${isDateSelected ? "check-circle" : "circle"}`} />
                </div>
              )}
              <div className="date-entry-header">
                <span className="date-entry-dot" />
                <span className="date-entry-label">
                  <i className="fas fa-calendar-alt" style={{ marginRight: 6, opacity: 0.6 }} />
                  {dateEntry.date}
                </span>
                {isEntryExpired && (
                  <span className="date-expiry-badge date-expiry-badge--expired">
                    <i className="fas fa-lock" /> Expirado
                  </span>
                )}
                {!isEntryExpired &&
                  selectedProject.is_free_plan &&
                  entryDaysRemaining !== undefined &&
                  entryDaysRemaining !== null &&
                  entryDaysRemaining > 0 && (
                    <span
                      className={`date-expiry-badge ${
                        isExpiringSoon ? "date-expiry-badge--warning" : "date-expiry-badge--info"
                      }`}
                    >
                      <i className="fas fa-clock" /> {entryDaysRemaining} dia
                      {entryDaysRemaining !== 1 ? "s" : ""} restantes
                    </span>
                  )}
              </div>

              {isEntryExpired ? (
                <div className="date-entry-expired-msg">
                  <i className="fas fa-lock" />
                  <span>Este conteúdo expirou após 30 dias no plano gratuito.</span>
                  <button className="date-entry-upgrade-btn" onClick={() => navigate("/plans")}>
                    Fazer upgrade
                  </button>
                </div>
              ) : (
              <div className="date-entry-body">
                {/* Gemeo progress */}
                {dateEntry.products?.includes("gemeo_digital") && gemeoProgress !== undefined && gemeoProgress < 100 && (
                  <div className={`gemeo-status-bar ${gemeoProgress === -1 ? "error" : "processing"}`}>
                    <i className={`fas fa-${gemeoProgress === -1 ? "exclamation-circle" : "cog fa-spin"}`} />
                    {gemeoProgress === -1
                      ? "Erro no processamento do Gêmeo Digital"
                      : `Processando Gêmeo Digital… ${gemeoProgress}%`}
                  </div>
                )}

                {/* BIM progress */}
                {dateEntry.products?.includes("bim") && bimProgress !== undefined && bimProgress < 100 && (
                  <div className={`gemeo-status-bar ${bimProgress === -1 ? "error" : "processing"}`}>
                    <i className={`fas fa-${bimProgress === -1 ? "exclamation-circle" : "cog fa-spin"}`} />
                    {bimProgress === -1
                      ? "Erro no processamento do BIM"
                      : `Processando BIM… ${bimProgress}%`}
                  </div>
                )}

                {/* Products */}
                <div className="product-actions-grid">
                  {dateEntry.products?.filter(product => typeof product === "string").map(product => {
                    const folder_path  = `projetos/${selectedProject.name}/${dateEntry.date}/${product}/`;
                    const productKey   = `${selectedProject.name}-${dateEntry.date}-${product}`;
                    const isLoadingShare    = loadingShares[productKey];
                    const isLoadingDownload = loadingDownloads[productKey];
                    const isShareable  = ["fotos","filmagens","gemeo_digital","tour_360","geoview","ortomosaico","geotiff","bim"].includes(product);
                    const colors       = PRODUCT_COLORS[product] || DEFAULT_COLOR;
                    const isDemo       = selectedProject.name.toLowerCase() === "demo";

                    return (
                      <div key={product} className="product-action-group">
                        <button
                          className="product-action-btn"
                          style={{ background: colors.bg, borderColor: colors.border }}
                          onClick={() => handleOpenProduct(selectedProject.name, dateEntry.date, product)}
                        >
                          <span className="product-action-btn-emoji">
                            {PRODUCT_LABELS[product]?.split(" ")[0]}
                          </span>
                          <span className="product-action-btn-label">
                            {PRODUCT_LABELS[product]?.split(" ").slice(1).join(" ") || product}
                          </span>
                        </button>

                        {isShareable && !isDemo && (
                          isLoadingShare ? (
                            <div className="product-side-icon">
                              <div className="product-spinner" />
                            </div>
                          ) : (
                            <Tooltip title="Compartilhar — Gerar link para compartilhar esta pasta" arrow placement="top">
                              <button
                                className="product-side-icon"
                                onClick={() => handleShare(folder_path, productKey, product, selectedProject.name)}
                              >
                                <i className="fas fa-share-alt" />
                              </button>
                            </Tooltip>
                          )
                        )}

                        {product !== "relatorios" && !isDemo && (
                          loadingTransfers[productKey] ? (
                            <div className="product-side-icon">
                              <div className="product-spinner" />
                            </div>
                          ) : (
                            <Tooltip title="Transferir — Enviar este produto para outro usuário" arrow placement="top">
                              <button
                                className="product-side-icon"
                                onClick={() => handleTransferProduct(selectedProject.name, dateEntry.date, product)}
                              >
                                <i className="fas fa-share-square" />
                              </button>
                            </Tooltip>
                          )
                        )}

                        {!isDemo && product !== "gemeo_digital" && product !== "relatorios" && (
                          isLoadingDownload ? (
                            <div className="product-side-icon">
                              <div className="product-spinner" />
                            </div>
                          ) : (
                            <Tooltip title="Baixar — Fazer download dos arquivos deste produto" arrow placement="top">
                              <button
                                className="product-side-icon"
                                onClick={() => handleDownload(selectedProject.name, dateEntry.date, product, productKey)}
                              >
                                <i className="fas fa-download" />
                              </button>
                            </Tooltip>
                          )
                        )}

                        {!isDemo && (
                          <Tooltip title="Excluir — Remover este produto do projeto" arrow placement="top">
                            <button
                              className="product-side-icon danger"
                              onClick={() => handleDeleteProduct(selectedProject.name, dateEntry.date, product)}
                            >
                              <i className="fas fa-trash-alt" />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              )}

              <div className="date-entry-footer">
                {!dateSelectionMode && !isDemo && (canQuickTransfer || canQuickDelete) && (
                  <div className="date-card-quick-actions">
                    {canQuickTransfer && (
                      <Tooltip title="Transferir" arrow placement="top">
                        <button
                          type="button"
                          className="cqa-btn"
                          onClick={e => {
                            e.stopPropagation();
                            handleTransferDate(dateEntry);
                          }}
                        >
                          <i className="fas fa-share-square" />
                        </button>
                      </Tooltip>
                    )}

                    {canQuickDelete && (
                      <Tooltip title="Excluir" arrow placement="top">
                        <button
                          type="button"
                          className="cqa-btn cqa-btn--danger"
                          onClick={e => {
                            e.stopPropagation();
                            handleDeleteDate(dateEntry);
                          }}
                        >
                          <i className="fas fa-trash-alt" />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        </div>{/* /dates-grid */}

        {dateSelectionMode && !isDemo && selectedDateKeys.size > 0 && (
          <div className="bulk-toolbar" role="region" aria-label="Ações em lote">
            <span className="bulk-toolbar-count">
              <i className="fas fa-check-square" /> {selectedDateKeys.size} selecionado(s)
            </span>
            <div className="bulk-toolbar-actions">
              <button className="bulk-toolbar-btn bulk-toolbar-btn--danger" onClick={handleDateBulkDelete}>
                <i className="fas fa-trash-alt" /> Excluir
              </button>
              <button className="bulk-toolbar-btn bulk-toolbar-btn--transfer" onClick={handleDateBulkTransfer}>
                <i className="fas fa-share-square" /> Transferir
              </button>
              <button
                className="bulk-toolbar-btn bulk-toolbar-btn--cancel"
                onClick={() => {
                  setDateSelectionMode(false);
                  setSelectedDateKeys(new Set());
                }}
              >
                <i className="fas fa-times" /> Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  /* ── Loading ──────────────────────────────────────────────────────────── */
  if (loading) return (
    <div className="projects-loading">
      <div className="projects-loading-ring" />
      <p>Carregando projetos…</p>
    </div>
  );

  if (error) return <p style={{ padding: 24, color: "var(--text-2)" }}>{error}</p>;

  /* ── Root ─────────────────────────────────────────────────────────────── */
  return (
    <div className="projects-page">
      <PageHeader
        title="Meus projetos"
        subtitle="Visualize e gerencie todos os seus projetos desenvolvidos."
        accent="Projetos"
      />

      <div className="projects-scroll-area">
        {view === "list" ? renderListView() : renderProjectView()}
      </div>

      {/* ── Share Link Modal ─────────────────────────────────────────── */}
      <AppModal
        isOpen={shareModal.open}
        onClose={() => setShareModal({ open: false, url: "", copied: false })}
        allowOutsideClick={true}
        variant="custom"
        title="Link de Compartilhamento"
      >
        <p className="apm-url-label">URL gerada</p>
        <div className="apm-url-row">
          <input
            type="text"
            className="apm-url-input"
            value={shareModal.url}
            readOnly
            onClick={e => e.target.select()}
            aria-label="URL de compartilhamento"
          />
          <button
            type="button"
            className={`apm-copy-btn${shareModal.copied ? " apm-copy-btn--copied" : ""}`}
            onClick={handleCopyShareUrl}
          >
            {shareModal.copied
              ? <><i className="fas fa-check" /> Copiado!</>
              : <><i className="fas fa-copy" /> Copiar</>}
          </button>
        </div>
      </AppModal>

      {/* ── General Modal ────────────────────────────────────────────── */}
      <AppModal
        isOpen={modal.open}
        onClose={closeModal}
        allowOutsideClick={modal.variant !== "loading"}
        variant={modal.variant}
        title={modal.title}
        message={modal.message}
        confirmText={modal.confirmText}
        onConfirm={modal.onConfirm ?? confirmModal}
        cancelText={modal.cancelText}
        onCancel={closeModal}
        showCloseBtn={modal.variant !== "loading"}
      />

      {/* ── Transfer Modal ───────────────────────────────────────────── */}
      <AppModal
        isOpen={transferModal.open}
        onClose={() => setTransferModal(m => ({ ...m, open: false }))}
        allowOutsideClick={true}
        variant="custom"
        title="Transferir produto"
        confirmText="Transferir"
        onConfirm={handleTransferConfirm}
        cancelText="Cancelar"
        onCancel={() => setTransferModal(m => ({ ...m, open: false }))}
      >
        <p style={{ fontSize: "0.875rem", color: "var(--apm-text, #475569)", marginBottom: 10 }}>
          Informe o e-mail do destinatário para <strong>{transferModal.projectName}</strong> — {PRODUCT_LABELS[transferModal.product] || transferModal.product}:
        </p>
        <input
          type="email"
          autoComplete="off"
          placeholder="email@exemplo.com"
          value={transferModal.email}
          onChange={e => setTransferModal(m => ({ ...m, email: e.target.value }))}
          style={{
            width: "100%", padding: "8px 12px", borderRadius: 8,
            border: "1px solid #e2e8f0", fontSize: "0.875rem", outline: "none",
            boxSizing: "border-box"
          }}
        />
      </AppModal>

      {/* ── Bulk Transfer Modal ───────────────────────────────────────── */}
      <AppModal
        isOpen={bulkTransferModal.open}
        onClose={() => setBulkTransferModal(m => ({ ...m, open: false }))}
        allowOutsideClick={true}
        variant="custom"
        title={`Transferir ${
          bulkTransferModal.mode === "dates" ? selectedDateKeys.size : selectedNames.size
        } ${bulkTransferModal.mode === "dates" ? "data(s)" : "projeto(s)"}`}
        confirmText="Transferir todos"
        onConfirm={handleBulkTransferConfirm}
        cancelText="Cancelar"
        onCancel={() => setBulkTransferModal(m => ({ ...m, open: false }))}
      >
        <p style={{ fontSize: "0.875rem", color: "var(--apm-text, #475569)", marginBottom: 10 }}>
          Informe o e-mail do destinatário. Todos os produtos (exceto relatórios) ${
            bulkTransferModal.mode === "dates" ? "das datas selecionadas" : "dos projetos selecionados"
          } serão transferidos:
        </p>
        <input
          type="email"
          autoComplete="off"
          placeholder="email@exemplo.com"
          value={bulkTransferModal.email}
          onChange={e => setBulkTransferModal(m => ({ ...m, email: e.target.value }))}
          style={{
            width: "100%", padding: "8px 12px", borderRadius: 8,
            border: "1px solid #e2e8f0", fontSize: "0.875rem", outline: "none",
            boxSizing: "border-box"
          }}
        />
      </AppModal>
    </div>
  );
};

const mapStateToProps = state => ({
  email: state.auth.email,
  token: state.auth.accessToken
});

export default connect(mapStateToProps)(Projects);
